import { inject, Injectable } from '@angular/core';
import { BooleanOperator, ServiceType } from '@common/enums';
import { ServiceFields, User, UserInfoForService } from '@common/models';
import { ODataServiceFactory } from '@common/odata';
import { containsWrapper } from '@common/utils/functions/search';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UserInfoService {
  private readonly odataFactory = inject(ODataServiceFactory);

  private readonly usersOdata = this.odataFactory.CreateService<User>('BackupUsers');

  /*
   * @param serviceType
   * @param search - contains in user email
   * @param includeUsersId - array of user isd that a return object should contain
   */
  loadUsers(serviceType: ServiceType, search: string = null, includeUserIds: string[] = []): Observable<UserInfoForService[]> {
    const fields = new ServiceFields(serviceType);
    const baseFields = ['Email', 'Id', 'InBackup', 'Name', 'UserProviderState'];
    const selectedFields = [...baseFields, fields.ServiceTypeStr].toSorted();

    const filters: string[] = [];

    if (search) {
      filters.push(`(${containsWrapper('Email', search)} ${BooleanOperator.Or} ${containsWrapper('Name', search)})`);
    }

    const query = this.usersOdata.Query().Top(500).Select(selectedFields);
    const users$ = query.Filter(filters.join(` ${BooleanOperator.And} `)).Exec();
    const selectedUsers$ = includeUserIds.length > 0 ? query.Filter(`Id in (${includeUserIds.join(', ')})`).Exec() : of<User[]>([]);

    return forkJoin({
      users: users$,
      selectedUsers: selectedUsers$
    }).pipe(
      map(({ users, selectedUsers }) => this.mergeSelectedUsers(users, selectedUsers, !!search)),
      map((users) => users.map((user) => this.mapToServiceInfo(user, fields)))
    );
  }

  private mergeSelectedUsers(users: User[], selectedUsers: User[], hasSearch: boolean): User[] {
    if (hasSearch || !selectedUsers.length) {
      return users;
    }

    const usersMap = new Map(users.map((u) => [u.Id, u]));

    return [...selectedUsers.filter((u) => !usersMap.has(u.Id)), ...users];
  }

  private mapToServiceInfo(user: User, fields: ServiceFields): UserInfoForService {
    const { Email, Id, InBackup, Name, UserProviderState } = user;

    return {
      Email,
      Id,
      InBackup,
      Name,
      LastBackupDate: user[fields.ServiceTypeStr].LastBackupInfo?.Date,
      TotalSize: user[fields.ServiceTypeStr].Size || 0,
      UserProviderState
    };
  }
}
